// libraries
import React, { useState, useId, useRef, useEffect } from 'react'
import clsx from 'clsx'
import { Swiper, SwiperSlide } from 'swiper/react'
import type { Swiper as SwiperType } from 'swiper'
import { Mousewheel, Navigation } from 'swiper/modules'
import 'swiper/css'

// components
import Section from '/react4xp/components/Section'
import Button from '/react4xp/components/Button'
import ReelsCard, { ReelsCardRef } from '/react4xp/components/ReelsCard'

// svg
import { UxArrowRight, UxArrowLeft } from '/react4xp/components/Svg/Ux/Arrow'

interface ReelsSliderProps {
	lazyLoadVideo?: boolean
    componentId?: string
    title?: string
    intro?: string
    link?: {
        label?: string
        href?: string
        external?: boolean
    }
    cards?: {
		youtube?: string
		vimeo?: string
		title?: string
    }[]
    singleStrings?: {
        previous?: string
        next?: string
    }
}

export default function ReelsSlider({
	lazyLoadVideo = false,
    componentId,
    title,
    intro,
    link,
    cards,
    singleStrings
}: ReelsSliderProps) {

	const uniqueId = useId()
    const swiperRef = useRef<SwiperType | null>(null)
    const prevButtonRef = useRef<HTMLButtonElement>(null)
    const nextButtonRef = useRef<HTMLButtonElement>(null)
    const cardRefs = useRef<(ReelsCardRef | null)[]>([])
    const [currentSlide, setCurrentSlide] = useState(0)
    const [isMobile, setIsMobile] = useState(false)

    const sanitizedId = (componentId || uniqueId.replace(/:/g, '')).replace(/[^a-zA-Z0-9_-]/g, '-')
    const prevButtonClass = `reels-slider__prev-${sanitizedId}`
    const nextButtonClass = `reels-slider__next-${sanitizedId}`
    
    // check if screen is below 768px
    useEffect(() => {
        const checkScreenSize = () => {
            setIsMobile(window.innerWidth < 768)
        }
        
        checkScreenSize()
        window.addEventListener('resize', checkScreenSize)
        
        return () => window.removeEventListener('resize', checkScreenSize)
    }, [])

    // handle video playing based on screen size and current slide
    const playVideoOnSlide = (slideIndex: number) => {
        if (!cards) return

        cardRefs.current.forEach((cardRef, index) => {
            if (cardRef && cardRef.hasVideo()) {
                if (index === slideIndex) {
                    cardRef.playVideo();
                } else {
                    cardRef.pauseVideo();
                }
            }
        })
    }

    // play video when slide changes
    useEffect(() => {
        if (!lazyLoadVideo) {
            playVideoOnSlide(currentSlide)
        }
    }, [currentSlide])

    // handle mouse enter for desktop
    const handleMouseEnter = (index: number) => {
        if (!isMobile) {
            const hoveredCard = cardRefs.current[index]
            
            // only control videos if the hovered card has a video
            if (hoveredCard && hoveredCard.hasVideo()) {
                cardRefs.current.forEach((cardRef, cardIndex) => {
                    if (cardRef && cardRef.hasVideo()) {
                        if (cardIndex === index) {
                            cardRef.playVideo();
                        } else {
                            cardRef.pauseVideo();
                        }
                    }
                })
            }
        }
    }

	return (
		<Section
			bgColor='white'
			marginTop='big'
			marginBottom='big'
			className='reels-slider'
			children={
				<div className='container'>

					<div className='reels-slider__top'>

						<div className='reels-slider__top__left'>

							{title && (
								<h2 className='h1-hero-small'>
									{title}
								</h2>
							)}

							{intro && (
								<div
									className='text-big'
									dangerouslySetInnerHTML={{ __html: intro }}
									suppressHydrationWarning
								/>
							)}

							{link?.href && (
								<Button
									href={link.href}
									target={!!link.external}
									noUnderline
								>
									{link.label}
								</Button>
							)}

						</div>

						<div className={clsx(
							'reels-slider__top__right',
							(cards?.length === 2 && 'reels-slider__top__right--mobile-only') ||
							(cards?.length <= 1 && 'hidden')
						)}>

							<button
								ref={prevButtonRef}
								type='button'
								aria-label={singleStrings?.previous}
								className={`reels-slider__prev ${prevButtonClass}`}
							>
								<UxArrowLeft />
							</button>

							<button
								ref={nextButtonRef}
								type='button'
								aria-label={singleStrings?.next}
								className={`reels-slider__next ${nextButtonClass}`}
							>
								<UxArrowRight />
							</button>

						</div>

					</div>

					<div className='reels-slider__cards'>
						<Swiper
							modules={[Mousewheel, Navigation]}
							className='reels-slider__slider'
							spaceBetween={15}
							slidesPerView={1.2}
							initialSlide={0}
							autoHeight
							allowTouchMove
							onSwiper={(swiper) => {
								swiperRef.current = swiper
							}}
							onInit={(swiper) => {
								if (prevButtonRef.current && nextButtonRef.current) {
									swiper.navigation.init()
									swiper.navigation.update()
								}
							}}
							onSlideChange={(swiper) => {
								setCurrentSlide(swiper.activeIndex)
							}}
							mousewheel={{
								forceToAxis: true
							}}
							navigation={{
								nextEl: `.${nextButtonClass}`,
								prevEl: `.${prevButtonClass}`
							}}
							breakpoints={{
								768: {
									slidesPerView: 2,
									spaceBetween: 32
								},
								1200: {
									slidesPerView: 2.7
								}
							}}
						>
							{cards?.map((item, i) => (
								<SwiperSlide key={i}>
									<ReelsCard
										youtube={item?.youtube}
										vimeo={item?.vimeo}
										videoAutoplay={i === 0}
										title={item?.title}
										ref={(el: ReelsCardRef | null) => { cardRefs.current[i] = el }}
										onMouseEnter={() => handleMouseEnter(i)}
										lazyLoadVideo={lazyLoadVideo}
									/>
								</SwiperSlide>
							))}
						</Swiper>
					</div>

				</div>
			}
		/>
	)
}
